import { LOCATION_CHANGE, LocationChangeAction } from "connected-react-router";
import { parse, stringify } from "query-string";
import { all, put, select, takeEvery } from "redux-saga/effects";

import { ScrollToTop } from "@/mk2/components/ScrollToTop";
import { FormPageType } from "@/mk2/constants/enums";
import {
    MKReduxFormCancelPage,
    MKReduxFormClosePage,
    MKReduxFormOpenPage,
    MK_REDUX_FORM_CANCEL_PAGE,
    MK_REDUX_FORM_CLOSE_PAGE,
    MK_REDUX_FORM_OPEN_PAGE,
    mkReduxFormCancelPage,
} from "@/mk2/helpers/form.actions";
import { MKReduxFormState } from "@/mk2/helpers/form.reducers";
import { getSessionStorage } from "@/mk2/helpers/storages";
import { getFormState } from "@/mk2/reducers";
import {
    HistoryAction,
    HistoryLocationState,
    goBackInPWA,
    pushInPWA,
    replaceInPWA,
} from "@/mk2/services/browserHistory";

function openFormPage({ formPage, formName, history }: MKReduxFormOpenPage & HistoryAction) {
    const location = history.location;
    if (location.state?.currentPage === undefined) {
        replaceInPWA(history, {
            ...location,
            search: stringify({
                ...parse(location.search),
                // MS Edge has location.state === undefined when goBack()
                // so track state in query string
                fp: FormPageType.Main,
                fn: formName,
                fip: "1",
            }),
            state: {
                formIgnorePrompt: true,
                formName,
                currentPage: FormPageType.Main,
            },
        });
        const replaced = history.location;
        // We are pushing location change updates
        // and react can skip re-rendering (batching)
        // so fake ScrollToTop position save
        getSessionStorage().setItem(
            `ScrollToTop__scroll__${replaced.key}`,
            `${ScrollToTop.getScrollTop()}`,
        );
    }

    if (location.state?.currentPage !== formPage) {
        pushInPWA(history, {
            ...location,
            search: stringify({
                ...parse(location.search),
                // MS Edge has location.state === undefined when goBack()
                // so track state in query string
                fp: formPage,
                fn: formName,
                fip: "1",
            }),
            state: {
                formIgnorePrompt: true,
                formName,
                currentPage: formPage,
            },
        });
    }
}

function* cancelFormPage({ history }: MKReduxFormCancelPage & HistoryAction) {
    const location = history.location;
    const qs = parse(location.search, { parseNumbers: true });
    const formName = location.state?.formName || (qs.fn as string);
    if (!formName) {
        return; // Ignore
    }

    const formState: MKReduxFormState = yield select(getFormState, formName);
    const currentPage = location.state?.currentPage || (qs.fp as number) || FormPageType.Main;
    if (currentPage !== formState.currentFormPage) {
        const fallbackUrl = location.pathname; // remove query string and hash
        goBackInPWA(history, fallbackUrl);
    }
}

function closeFormPage({ history }: MKReduxFormClosePage & HistoryAction) {
    const fallbackUrl = location.pathname; // remove query string and hash
    goBackInPWA(history, fallbackUrl);
}

export default function* root() {
    yield all([
        takeEvery(MK_REDUX_FORM_OPEN_PAGE, openFormPage),
        takeEvery(MK_REDUX_FORM_CANCEL_PAGE, cancelFormPage),
        takeEvery(MK_REDUX_FORM_CLOSE_PAGE, closeFormPage),
    ]);
}
